import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  input,
  signal
} from "@angular/core";
import { Pet } from "src/app/backend/models/pet/pet";
import { sntTranslate } from "src/app/support/translate";
import { trim } from "lodash-es";
import { dateUtil } from "src/app/support/date-util";
import { AppointmentBackend } from "src/app/backend/api/appointment.backend";
import { lastValueFrom } from "rxjs";
import { AppointmentLight } from "src/app/backend/models/appointment/appointment";
import { Bind } from "src/app/support/decorators/bind";
import { BootstrapService } from "src/app/modules/bootstrap/bootstrap.service";
import { absoluteUrl } from "src/app/support/navigation";
import { TabService } from "src/app/services/tab.service";
import { sntConfirm } from "src/app/common/dialog";
import { TranslocoService } from "@jsverse/transloco";
import { PetBackend } from "src/app/backend/api/pet.backend";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { formatAge } from "src/app/support/format-age";
import dayjs from "dayjs";

@Component({
  selector: "pms-block-patient",
  templateUrl: "block-patient.component.html",
  styles: [
    `
      :host {
        display: flex;
      }
    `
  ],
  standalone: false
})
export class BlockPatientComponent implements OnInit {
  @Input()
  patient!: Partial<Pet>;

  @Input()
  aspectVideo: boolean = false;

  @Input()
  card: boolean = false;

  @Input()
  cardPadding: boolean = false;

  @Input()
  showLargeSpeciesIcon: boolean = false;

  @Input()
  showOwner: boolean = false;

  @Input()
  showNumber: boolean = false;

  @Input()
  showCreatedAt: boolean = false;

  @Input()
  widthFull: boolean = false;

  @Input()
  wrapPatientName: boolean = false;

  @Input()
  layout: "default" | "minimal" = "default";

  @Input()
  underlineName: boolean = false;

  @Input()
  showMicrochip: boolean = false;

  @Input()
  showSpeciesLabel: boolean = false;

  @Input()
  enableLastAppointmentDate: boolean = false;

  @Input()
  enableEdit: boolean = false;

  bgColor = input<string>("#fafafa");

  @Output()
  select: EventEmitter<void> = new EventEmitter<void>();

  labelSpeciesBreed: string | undefined;
  labelSex: "male" | "female" | undefined;
  labelAge: string | undefined;

  speciesIconClass: string | undefined;

  loadingLastAppointment = signal<boolean>(false);
  lastAppointmentDate = signal<string | undefined>(undefined);

  private readonly petBackend = inject(PetBackend);
  private readonly appointmentBackend = inject(AppointmentBackend);
  private readonly bootstrapService = inject(BootstrapService);
  private readonly tabService = inject(TabService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly transloco = inject(TranslocoService);

  ngOnInit(): void {
    this.onInitPetData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const patientChange = changes["patient"];

    if (patientChange && !patientChange.firstChange) {
      this.onInitPetData();
    }
  }

  onInitPetData(): void {
    if (this.showSpeciesLabel) {
      this.labelSpeciesBreed = this.patient.species
        ? sntTranslate(this.patient.species).toLowerCase()
        : undefined;
      this.labelAge = this.patient.birthday
        ? formatAge(this.patient.birthday)
        : undefined;
    } else {
      this.labelSpeciesBreed = trim(`${sntTranslate(this.patient.breed!)}`);
      this.labelAge = this.patient.birthday
        ? dayjs
            .duration(
              dateUtil(this.patient.birthday).diff(dateUtil(), "seconds"),
              "seconds"
            )
            .humanize(false)
        : undefined;
    }

    this.speciesIconClass = this.speciesIcon(this.patient);

    switch (this.patient.sex) {
      case "male": {
        this.labelSex = "male";
        break;
      }
      case "female": {
        this.labelSex = "female";
        break;
      }
      default: {
        this.labelSex = undefined;
        break;
      }
    }

    if (this.enableLastAppointmentDate) {
      this.loadingLastAppointment.set(true);
      this.changeDetectorRef.detectChanges();

      lastValueFrom(
        this.appointmentBackend.loadLastForPatient({
          patientId: this.patient!.id!
        })
      )
        .then((lastAppointment: AppointmentLight | undefined) => {
          if (lastAppointment) {
            this.lastAppointmentDate.set(
              dateUtil(lastAppointment.startAt).format("DD/MM/YYYY HH:mm")
            );
          }
          this.loadingLastAppointment.set(false);
          this.changeDetectorRef.detectChanges();
        })
        .catch(() => {
          this.loadingLastAppointment.set(false);
          this.changeDetectorRef.detectChanges();
        });
    }
  }

  speciesIcon(pet: Partial<Pet>): string {
    switch (pet.species) {
      case "canine":
      case "dog": {
        return "fa-light fa-dog";
      }
      case "cat": {
        return "fa-light fa-cat";
      }
      default: {
        return "fa-light fa-question-circle";
      }
    }
  }

  @Bind
  async onEdit(pet: Partial<Pet>): Promise<void> {
    if (this.bootstrapService.getCurrentProduct() === "PMS") {
      await this.tabService.open(
        absoluteUrl(`/organization/patients-registry/${pet.id}/edit`)
      );
    }
    if (this.bootstrapService.getCurrentProduct() === "Rocket") {
      await this.tabService.open(absoluteUrl(`/patients/${pet.id}/edit`));
    }
  }

  async onDelete(data: Partial<Pet>): Promise<void> {
    const isDeleted = await sntConfirm(
      this.transloco.translate(marker("Cancella paziente")),
      this.transloco.translate(
        marker("Sei sicuro di voler cancellare il paziente {{name}}?"),
        { name: data.name }
      )
    );
    if (isDeleted && data.id) {
      await lastValueFrom(this.petBackend.delete({ id: data.id }));
    }
  }
}
